import type { TimelineEntry } from "../../session-logic";

// Adapted from pingdotgg/t3code #11169. Held frames are display-only; they must
// never cross an authenticated user's organization boundary or retain blob URLs.
export interface HeldThreadTimeline {
  scopeKey: string;
  threadKey: string;
  entries: TimelineEntry[];
  markdownCwd?: string;
  workspaceRoot?: string;
}

const MAX_TIMELINES = 16;
const MAX_ENTRIES = 40_000;
const MAX_AGE_MS = 5 * 60_000;

export function createHeldThreadTimelineCache(now: () => number = Date.now) {
  const timelines = new Map<string, HeldThreadTimeline & { rememberedAt: number }>();
  let retainedEntries = 0;
  const remove = (key: string) => {
    retainedEntries -= timelines.get(key)?.entries.length ?? 0;
    timelines.delete(key);
  };
  const prune = () => {
    for (const [key, timeline] of timelines) {
      if (now() - timeline.rememberedAt >= MAX_AGE_MS) remove(key);
    }
  };
  return {
    remember(timeline: HeldThreadTimeline) {
      prune();
      // Drop prior identities, even when revisiting the same thread id.
      for (const [key, entry] of timelines) {
        if (entry.scopeKey !== timeline.scopeKey) remove(key);
      }
      remove(timeline.threadKey);
      if (
        timeline.entries.length === 0 ||
        timeline.entries.length > MAX_ENTRIES ||
        timeline.entries.some(
          (entry) =>
            entry.kind === "message" &&
            entry.message.attachments?.some(
              (attachment) =>
                attachment.type === "image" && attachment.previewUrl?.startsWith("blob:"),
            ),
        )
      )
        return;
      timelines.set(timeline.threadKey, { ...timeline, rememberedAt: now() });
      retainedEntries += timeline.entries.length;
      while (timelines.size > MAX_TIMELINES || retainedEntries > MAX_ENTRIES) {
        const oldest = timelines.keys().next().value;
        if (oldest === undefined) break;
        remove(oldest);
      }
    },
    resolve(
      scopeKey: string | null,
      threadKey: string,
      loading: boolean,
    ): HeldThreadTimeline | null {
      if (!scopeKey || !loading) return null;
      prune();
      const destination = timelines.get(threadKey);
      if (destination?.scopeKey === scopeKey) return destination;
      return (
        Array.from(timelines.values()).findLast((entry) => entry.scopeKey === scopeKey) ?? null
      );
    },
    clear() {
      timelines.clear();
      retainedEntries = 0;
    },
  };
}

export const heldThreadTimelines = createHeldThreadTimelineCache();
